import { useAcceptInvitation, useSession } from '@church/data'
import { faArrowsRotate, faCheck, faEnvelopeOpenText } from '@church/ui-icons'
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { Button, ErrorNote, Icon, Spinner } from '../components/ui.tsx'
import { ensureMe } from '../lib/session.ts'
import { currentSite, homeUrl, urlOn } from '../lib/site.ts'

/**
 * Invitation landing page (linked from the invite email). Signed-out visitors
 * detour through sign-in/up and come back here; the accept itself is explicit
 * — nobody should join an organization by merely opening a link.
 *
 * better-auth refuses acceptance until the invitee's email is verified (so a
 * mailbox squatter can't claim an invite meant for the address's real owner);
 * the unverified state gets guidance instead of a doomed button.
 */
export const Route = createFileRoute('/accept-invitation/$invitationId')({
  beforeLoad: async ({ context, location }) => {
    const me = await ensureMe(context.queryClient)
    if (!me) {
      // Signing up from an invitation comes back here, too.
      throw redirect({ to: '/sign-up', search: { redirect: location.pathname } })
    }
  },
  component: AcceptInvitationScreen,
})

function AcceptInvitationScreen() {
  const { invitationId } = Route.useParams()
  const navigate = useNavigate()
  const accept = useAcceptInvitation()
  const { me, isLoading, refetch } = useSession()

  return (
    <div className="flex min-h-full items-center justify-center bg-surface-sunken px-4">
      <div className="w-full max-w-md space-y-4 rounded-xl border border-border-subtle bg-surface-raised p-6 text-center shadow-sm sm:p-8">
        <Icon icon={faEnvelopeOpenText} className="mx-auto size-10 text-content-brand" />
        <h1 className="text-xl font-semibold text-content-default">You&rsquo;ve been invited</h1>

        {isLoading || !me ? (
          <Spinner />
        ) : !me.user.emailVerified ? (
          <>
            <p className="text-sm text-content-muted">
              First, verify your email address — we sent a link to{' '}
              <span className="font-medium">{me.user.email}</span>. Then come back here to accept.
            </p>
            <Button
              variant="secondary"
              icon={faArrowsRotate}
              className="w-full"
              onClick={() => void refetch()}
            >
              I&rsquo;ve verified — check again
            </Button>
          </>
        ) : (
          <>
            <p className="text-sm text-content-muted">
              Accept to join the organization with the role your inviter chose. If you weren&rsquo;t
              expecting this, just close the page.
            </p>

            <ErrorNote error={accept.error} />

            <Button
              icon={faCheck}
              className="w-full"
              disabled={accept.isPending}
              onClick={() => {
                accept.mutate(invitationId, {
                  // On id.forhim.io the church they joined opens in their home app:
                  // ForServices for a volunteer on a team, else ForChurch.
                  onSuccess: async () => {
                    const { data: joined } = await refetch()
                    const home = homeUrl(joined)
                    if (currentSite() !== 'id') return void navigate({ to: '/' })
                    window.location.assign(home.startsWith('/') ? urlOn('church', '/') : home)
                  },
                })
              }}
            >
              {accept.isPending ? 'Joining…' : 'Accept invitation'}
            </Button>
          </>
        )}
      </div>
    </div>
  )
}
